import { DiscountTypeEnum } from "@/features/shared";

/**
 * Every money figure on a sales return is derived from the returned goods, the
 * returned expenses and the tax/discount switches — none of it is independently
 * editable. Deriving it in one pure place keeps the form from storing values it
 * would then have to keep in sync, and lets the price summary and the submit
 * payload agree by construction.
 */

export const computeSubtotal = (returnableProductVariants = []) =>
  returnableProductVariants.reduce(
    (total, variant) =>
      total +
      (variant.price || 0) * (variant.quantity || 0) -
      (variant.discount || 0),
    0
  );

export const computeTotalExpense = (salesReturnExpenses = []) =>
  salesReturnExpenses.reduce(
    (total, expense) => total + (expense.amount || 0),
    0
  );

export const computeDiscount = ({ subtotal, discountType, discountAmount }) =>
  discountType === DiscountTypeEnum.PERCENTAGE && discountAmount
    ? (subtotal * discountAmount) / 100
    : discountAmount || 0;

/**
 * Returned expenses are added on top of the goods total, mirroring how the
 * invoice charged them. The backend derives the goods total back out by
 * subtracting them from grandTotal, so they must land after tax.
 */
export const computeSalesReturnTotals = ({
  returnableProductVariants,
  salesReturnExpenses,
  discountType,
  discountAmount,
  taxable,
  totalIncludesTax,
  taxPercentage,
}) => {
  const subtotal = computeSubtotal(returnableProductVariants);
  const totalExpense = computeTotalExpense(salesReturnExpenses);
  const discount = computeDiscount({ subtotal, discountType, discountAmount });

  let taxAmount = 0;
  let goodsTotal = subtotal - discount;

  if (taxable) {
    if (totalIncludesTax) {
      taxAmount = Math.round(
        (goodsTotal * taxPercentage) / (100 + taxPercentage)
      );
    } else {
      taxAmount = Math.round((goodsTotal * taxPercentage) / 100);
      goodsTotal += taxAmount;
    }
  }

  return {
    subtotal,
    totalExpense,
    discount,
    taxAmount,
    grandTotal: goodsTotal + totalExpense,
  };
};
